import { Rating, type Sentence } from '@/domain/Models';
import type { SentenceRepository, SentenceSessionView } from '@/domain/Repositories';
import { db } from './Database';

export class SentenceRepositoryImpl implements SentenceRepository {
  async list(query: string, favoritesOnly: boolean): Promise<Sentence[]> {
    const q = query.trim().toLowerCase();
    let rows = await db.examples.where('active').equals(1).toArray();
    if (q) {
      rows = rows.filter(
        (r) => r.textEn.toLowerCase().includes(q) || r.textZh.toLowerCase().includes(q),
      );
    }
    const favs = await db.itemMemory.where('itemType').equals('EXAMPLE').toArray();
    const fav = new Set(favs.filter((f) => f.isFavorite === 1).map((f) => f.itemId));
    let out = rows.map((r) => this.map(r, fav.has(r.id)));
    if (favoritesOnly) out = out.filter((s) => s.favorite);
    return out;
  }

  async get(sentenceId: string): Promise<Sentence | null> {
    const row = await db.examples.get(sentenceId);
    if (!row) return null;
    const mem = await db.itemMemory.get([sentenceId, 'EXAMPLE']);
    return this.map(row, mem?.isFavorite === 1);
  }

  async setFavorite(sentenceId: string, favorite: boolean): Promise<void> {
    const existing = await db.itemMemory.get([sentenceId, 'EXAMPLE']);
    const now = Date.now();
    await db.itemMemory.put({
      itemId: sentenceId,
      itemType: 'EXAMPLE',
      fsrsState: existing?.fsrsState ?? 'NEW',
      difficulty: existing?.difficulty ?? 0,
      stability: existing?.stability ?? 0,
      dueAtEpochMs: existing?.dueAtEpochMs ?? now,
      lastReviewAtEpochMs: existing?.lastReviewAtEpochMs ?? null,
      reps: existing?.reps ?? 0,
      lapses: existing?.lapses ?? 0,
      masteredUi: existing?.masteredUi ?? 0,
      learnedContentHash: existing?.learnedContentHash ?? null,
      updatedAtEpochMs: now,
      isFavorite: favorite ? 1 : 0,
    });
  }

  async todayQueue(): Promise<{ newCount: number; reviewCount: number; totalCount: number }> {
    const rows = await db.examples.where('active').equals(1).toArray();
    const mem = await db.itemMemory.where('itemType').equals('EXAMPLE').toArray();
    const byId = new Map(mem.map((m) => [m.itemId, m]));
    const now = Date.now();
    let fresh = 0;
    let due = 0;
    for (const r of rows) {
      const m = byId.get(r.id);
      if (!m) fresh += 1;
      else if (m.dueAtEpochMs <= now) due += 1;
    }
    return { newCount: fresh, reviewCount: due, totalCount: fresh + due };
  }

  async startOrResumeSession(): Promise<string> {
    const open = await db.sentenceCheckpoints
      .where('status')
      .equals('IN_PROGRESS')
      .reverse()
      .sortBy('updatedAtEpochMs');
    const existing = open.at(-1);
    if (existing) return existing.sessionId;
    const examples = await db.examples.where('active').equals(1).toArray();
    if (examples.length === 0) throw new Error('NO_SENTENCES');
    const sessionId = `sent-${Date.now()}`;
    await db.sentenceCheckpoints.put({
      sessionId,
      status: 'IN_PROGRESS',
      queueItemIdsJson: JSON.stringify(examples.map((e) => e.id)),
      currentIndex: 0,
      answerRevealed: 0,
      selectedRating: null,
      startedAtEpochMs: Date.now(),
      updatedAtEpochMs: Date.now(),
    });
    return sessionId;
  }

  async session(sessionId: string): Promise<SentenceSessionView | null> {
    const row = await db.sentenceCheckpoints.get(sessionId);
    if (!row) return null;
    const ids = JSON.parse(row.queueItemIdsJson) as string[];
    const id = ids[row.currentIndex];
    const example = id ? await db.examples.get(id) : undefined;
    return {
      sessionId: row.sessionId,
      currentIndex: row.currentIndex,
      totalCount: ids.length,
      example: example
        ? {
            id: example.id,
            textEn: example.textEn,
            textZh: example.textZh,
            audioAssetId: example.audioAssetId,
            industry: example.industry,
            scene: example.scene,
          }
        : null,
      answerRevealed: row.answerRevealed === 1,
      selectedRating: (row.selectedRating as Rating | null) ?? null,
    };
  }

  async revealAnswer(sessionId: string): Promise<void> {
    const row = await db.sentenceCheckpoints.get(sessionId);
    if (!row) return;
    await db.sentenceCheckpoints.update(sessionId, { answerRevealed: 1, updatedAtEpochMs: Date.now() });
  }

  async recordReview(
    sessionId: string,
    itemId: string,
    expectedIndex: number,
    rating: Rating,
  ): Promise<{ done: boolean }> {
    const row = await db.sentenceCheckpoints.get(sessionId);
    if (!row) throw new Error('SESSION_NOT_FOUND');
    if (row.currentIndex !== expectedIndex) throw new Error('INDEX_MISMATCH');
    const ids = JSON.parse(row.queueItemIdsJson) as string[];
    if (ids[expectedIndex] !== itemId) throw new Error('ITEM_MISMATCH');
    const next = expectedIndex + 1;
    const done = next >= ids.length;
    await db.sentenceCheckpoints.update(sessionId, {
      currentIndex: done ? expectedIndex : next,
      answerRevealed: 0,
      selectedRating: rating,
      status: done ? 'COMPLETED' : 'IN_PROGRESS',
      updatedAtEpochMs: Date.now(),
    });
    return { done };
  }

  private map(
    r: {
      id: string;
      industry: string;
      scene: string;
      speaker: string;
      textEn: string;
      textZh: string;
      linkedTermIdsJson: string;
      dialogueGroupId: string | null;
      audioAssetId: string;
      contentHash: string;
    },
    favorite: boolean,
  ): Sentence {
    return {
      id: r.id,
      industry: r.industry,
      scene: r.scene,
      speaker: r.speaker,
      textEn: r.textEn,
      textZh: r.textZh,
      linkedTermIds: JSON.parse(r.linkedTermIdsJson) as string[],
      dialogueGroupId: r.dialogueGroupId,
      audioAssetId: r.audioAssetId,
      contentHash: r.contentHash,
      favorite,
    };
  }
}
